import React, { useContext, useState, useEffect } from 'react';
import _ from 'lodash';
import { useSnackbar } from 'notistack';
import {
    Box,
    tabsClasses,
    Tab,
    Tabs,
} from '@mui/material';

import { useFetch } from '../components/auth/useFetch';
import PermissionGuard from '../contexts/PermissionContext';
import ProjectContext from "../contexts/ProjectContext";

import ControlBar from '../components/structure/ControlBar';
import ControlDivider from '../components/structure/ControlDivider';
import SaveButtonGroup from '../components/buttons/SaveButtonGroup';

import GeneralSettings from '../components/settings/GeneralSettings';
import ProjectSettings from '../components/settings/ProjectSettings';
import CrewSettings from '../components/settings/CrewSettings';
import EditorialEmailRecipientsSettings from '../components/settings/EditorialEmailRecipientsSettings';
import AvidPanelSettings from '../components/settings/AvidPanelSettings';
import MetadataSourceSettings from '../components/settings/MetadataSourceSettings';
import TrackerSettings from '../components/settings/TrackerSettings';
import VFXPullsSettings from '../components/settings/VFXPullsSettings';
import VFXVendorsSettings from '../components/settings/VFXVendorsSettings';
import VFXCameraFormatsSettings from '../components/settings/VFXCameraFormatsSettings';


export default function SettingsView() {
    const { enqueueSnackbar } = useSnackbar();
    const projectContext = useContext(ProjectContext);
    const fetchWithAuth = useFetch();

    const [editedSettings, setEditedSettings] = useState({});
    const [settings, setSettings] = useState({});
    const [settingsSchema, setSettingsSchema] = useState({});
    const [tabs, setTabs] = useState(['Konsol', 'Illusion'])
    const [value, setValue] = useState(0);


    useEffect(() => {
        loadSettings()
    }, [])

    function discardSettings() {
        const originalSettings = _.cloneDeep(settings);
        setSettings(originalSettings)
        setEditedSettings(originalSettings)
    }

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    async function loadSettings(){
        await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/konsol/settings/meta`, {
            headers:{
                'X-Project-ID': projectContext?.project_db
            },
        })
            .then((res) => res.json())
            .then((result) => setSettingsSchema(result))
            .catch(e => {
                console.error(e);
            })

        await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/konsol/settings`, {
            headers:{
                'X-Project-ID': projectContext?.project_db
            },
        })
            .then((res) => res.json())
            .then(
                (result) => {
                    setSettings(result);
                    setEditedSettings(result);
                },
            )
            .catch(e => {
                console.error(e);
            })
    }

    async function saveSettings() {
        try{
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/konsol/settings/`, {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    'X-Project-ID': projectContext?.project_db
                },
                body: JSON.stringify(editedSettings),
                redirect: 'follow',
            })
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            setSettings(editedSettings);
        } catch (error) {
            enqueueSnackbar(`Failed to save settings`, { variant: 'error' });
        }
    }

    return (
        <React.Fragment>

            <Box
                display="flex"
                flexDirection="column"
                height='100%'
                width='100%'
                overflow='hidden'
            >
                <Box width='100%'>
                    <ControlBar>
                        <Box sx={{ maxWidth: { xs: 320, sm: 880 } }}>
                            <Tabs
                            value={value}
                            onChange={handleChange}
                            indicatorColor="secondary"
                            textColor="white"
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{
                                [`& .${tabsClasses.scrollButtons}`]: {
                                '&.Mui-disabled': { opacity: 0.2 },
                                },
                            }}
                            >
                                {tabs.map((tab, index) => (
                                    <Tab
                                        key={index}
                                        label={tab}
                                        style={{ textTransform: 'none', fontWeight: 'bold' }} />
                                ))}
                            </Tabs>
                        </Box>
                        <ControlDivider />
                        <Box display="flex" alignItems="center">
                            <SaveButtonGroup
                                disabled={_.isEqual(settings, editedSettings)}
                                onDiscardClick={discardSettings}
                                onSaveClick={saveSettings}
                            />
                        </Box>
                    </ControlBar>
                </Box>

                <Box
                    overflow='auto'
                    height='100%'
                    backgroundColor='rgba(0,0,0,0.9)'
                >
                    {
                        // SHOW KONSOL SETTINGS
                        value === 0 
                        ?
                            <Box
                                display="flex"
                                flexDirection="column"
                                gap={2}
                                p={2}
                            >
                                <PermissionGuard
                                    permission={(userPermissions) => {
                                        const schema = Object.entries(settingsSchema).find(([key]) => key === 'general')?.[1] ?? {};
                                        const allPermissions = [
                                            ...(schema.permissions?.read ?? []),
                                            ...(schema.permissions?.write ?? [])
                                        ];
                                        return allPermissions.some(perm => userPermissions.includes(perm));
                                    }}
                                >
                                    <GeneralSettings
                                        schema={Object.entries(settingsSchema).find(([key, schema]) => key === 'general')?.at(1) ?? {}}
                                        setSettings={(newSettings) => {
                                            const newEditedSettings = { ...editedSettings, general: newSettings };
                                            setEditedSettings(newEditedSettings);
                                        }}
                                        settings={Object.entries(settings).find(([key, settings]) => key === 'general')?.at(1) ?? {}}
                                    />
                                </PermissionGuard>
                                <PermissionGuard
                                    permission={(userPermissions) => {
                                        const schema = Object.entries(settingsSchema).find(([key]) => key === 'project')?.[1] ?? {};
                                        const allPermissions = [
                                            ...(schema.permissions?.read ?? []),
                                            ...(schema.permissions?.write ?? [])
                                        ];
                                        return allPermissions.some(perm => userPermissions.includes(perm));
                                    }}
                                >
                                    <ProjectSettings
                                        schema={Object.entries(settingsSchema).find(([key, schema]) => key === 'project')?.at(1) ?? {}}
                                        setSettings={(newSettings) => {
                                            const newEditedSettings = { ...editedSettings, project: newSettings };
                                            setEditedSettings(newEditedSettings);
                                        }}
                                        settings={Object.entries(settings).find(([key, settings]) => key === 'project')?.at(1) ?? {}}

                                    />
                                </PermissionGuard>
                                <PermissionGuard
                                    permission={(userPermissions) => {
                                        const schema = Object.entries(settingsSchema).find(([key]) => key === 'crew')?.[1] ?? {};
                                        const allPermissions = [
                                            ...(schema.permissions?.read ?? []),
                                            ...(schema.permissions?.write ?? [])
                                        ];
                                        return allPermissions.some(perm => userPermissions.includes(perm));
                                    }}
                                >
                                    <CrewSettings
                                        schema={Object.entries(settingsSchema).find(([key, schema]) => key === 'crew')?.at(1) ?? {}}
                                        setSettings={(newSettings) => {
                                            const newEditedSettings = { ...editedSettings, crew: newSettings };
                                            setEditedSettings(newEditedSettings);
                                        }}
                                        settings={Object.entries(settings).find(([key, settings]) => key === 'crew')?.at(1) ?? {}}
                                    />
                                </PermissionGuard>
                                <PermissionGuard
                                    permission={(userPermissions) => {
                                        const schema = Object.entries(settingsSchema).find(([key]) => key === 'sources')?.[1] ?? {};
                                        const allPermissions = [
                                            ...(schema.permissions?.read ?? []),
                                            ...(schema.permissions?.write ?? [])
                                        ];
                                        return allPermissions.some(perm => userPermissions.includes(perm));
                                    }}
                                >
                                    <MetadataSourceSettings
                                        schema={Object.entries(settingsSchema).find(([key, schema]) => key === 'sources')?.at(1) ?? {}}
                                        setSettings={(newSettings) => {
                                            const newEditedSettings = { ...editedSettings, sources: newSettings };
                                            setEditedSettings(newEditedSettings);
                                        }}
                                        settings={Object.entries(settings).find(([key, settings]) => key === 'sources')?.at(1) ?? {}}
                                    />
                                </PermissionGuard>
                                <PermissionGuard
                                    permission={(userPermissions) => {
                                        const schema = Object.entries(settingsSchema).find(([key]) => key === 'tracker')?.[1] ?? {};
                                        const allPermissions = [
                                            ...(schema.permissions?.read ?? []),
                                            ...(schema.permissions?.write ?? [])
                                        ];
                                        return allPermissions.some(perm => userPermissions.includes(perm));
                                    }}
                                >
                                    <TrackerSettings
                                        schema={Object.entries(settingsSchema).find(([key, schema]) => key === 'tracker')?.at(1) ?? {}}
                                        setSettings={(newSettings) => {
                                            const newEditedSettings = { ...editedSettings, tracker: newSettings };
                                            setEditedSettings(newEditedSettings);
                                        }}
                                        settings={Object.entries(settings).find(([key, settings]) => key === 'tracker')?.at(1) ?? {}}
                                    />
                                </PermissionGuard>   
                                <PermissionGuard
                                    permission={(userPermissions) => {
                                        const schema = Object.entries(settingsSchema).find(([key]) => key === 'editorial')?.[1] ?? {};
                                        const allPermissions = [
                                            ...(schema.permissions?.read ?? []),
                                            ...(schema.permissions?.write ?? [])
                                        ];
                                        return allPermissions.some(perm => userPermissions.includes(perm));
                                    }}
                                >
                                    <EditorialEmailRecipientsSettings
                                        schema={Object.entries(settingsSchema).find(([key, schema]) => key === 'editorial')?.at(1) ?? {}}
                                        setSettings={(newSettings) => {
                                            const newEditedSettings = { ...editedSettings, editorial: newSettings };
                                            setEditedSettings(newEditedSettings);
                                        }}
                                        settings={Object.entries(settings).find(([key, settings]) => key === 'editorial')?.at(1) ?? {}}
                                    />
                                    <AvidPanelSettings
                                        schema={Object.entries(settingsSchema).find(([key, schema]) => key === 'avid_panel')?.at(1) ?? {}}
                                        setSettings={(newSettings) => {
                                            const newEditedSettings = { ...editedSettings, avid_panel: newSettings };
                                            setEditedSettings(newEditedSettings);
                                        }}
                                        settings={Object.entries(settings).find(([key, settings]) => key === 'avid_panel')?.at(1) ?? {}}
                                    />
                                </PermissionGuard>  

                            </Box>

                        // SHOW ILLUSION SETTINGS
                        : value === 1 ?
                            <Box
                                display="flex"
                                flexDirection="column"
                                gap={2}
                                p={2}
                            >
                                <PermissionGuard
                                    permission={(userPermissions) => {
                                        const schema = Object.entries(settingsSchema).find(([key]) => key === 'vfxpulls')?.[1] ?? {};
                                        const allPermissions = [
                                            ...(schema.permissions?.read ?? []),
                                            ...(schema.permissions?.write ?? [])
                                        ];
                                        return allPermissions.some(perm => userPermissions.includes(perm));
                                    }}
                                >
                                    <VFXPullsSettings
                                        schema={Object.entries(settingsSchema).find(([key, schema]) => key === 'vfxpulls')?.at(1) ?? {}}
                                        setSettings={(newSettings) => {
                                            const newEditedSettings = { ...editedSettings, vfxpulls: newSettings };
                                            setEditedSettings(newEditedSettings);
                                        }}
                                        settings={Object.entries(settings).find(([key, settings]) => key === 'vfxpulls')?.at(1) ?? {}}
                                    />
                                </PermissionGuard>
                                <PermissionGuard
                                    permission={(userPermissions) => {
                                        const schema = Object.entries(settingsSchema).find(([key]) => key === 'vfxpulls')?.[1] ?? {};
                                        const allPermissions = [
                                            ...(schema.permissions?.read ?? []),
                                            ...(schema.permissions?.write ?? [])
                                        ];
                                        return allPermissions.some(perm => userPermissions.includes(perm));
                                    }}
                                >
                                    <VFXVendorsSettings
                                        schema={Object.entries(settingsSchema).find(([key, schema]) => key === 'vfxpulls')?.at(1) ?? {}}
                                        setSettings={(newSettings) => {
                                            const newEditedSettings = { ...editedSettings, vfxpulls: newSettings };
                                            setEditedSettings(newEditedSettings);
                                        }}
                                        settings={Object.entries(settings).find(([key, settings]) => key === 'vfxpulls')?.at(1) ?? {}}
                                    />
                                </PermissionGuard>
                                {/* <PermissionGuard
                                    permission={(userPermissions) => {
                                        const schema = Object.entries(settingsSchema).find(([key]) => key === 'vfxpulls')?.[1] ?? {};
                                        const allPermissions = [
                                            ...(schema.permissions?.read ?? []),
                                            ...(schema.permissions?.write ?? [])
                                        ];
                                        return allPermissions.some(perm => userPermissions.includes(perm));
                                    }}
                                >
                                    <VFXCameraFormatsSettings
                                        schema={Object.entries(settingsSchema).find(([key, schema]) => key === 'vfxpulls')?.at(1) ?? {}}
                                        setSettings={(newSettings) => {
                                            const newEditedSettings = { ...editedSettings, vfxpulls: newSettings };
                                            setEditedSettings(newEditedSettings);
                                        }}
                                        settings={Object.entries(settings).find(([key, settings]) => key === 'vfxpulls')?.at(1) ?? {}}
                                    />
                                </PermissionGuard> */}
                            </Box>
                        :
                        <></>
                    }
                </Box>
            </Box>
        </React.Fragment>
    )
}